<!-- Grouping wrapper for Radio options with shared label and validation errors. -->

<script lang="ts" setup generic="T extends Primitive = Primitive">
import type { Primitive } from '@nzyme/types/Common.js';
import { getRadioGroupEmits, getRadioGroupProps, useRadioGroup } from '@nzyme/vue-forms/components/useRadioGroup.js';
import { classProp } from '@nzyme/vue-utils/classProp.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import { computed, useCssModule, useId } from 'vue';

import FieldInfo from './FieldInfo.vue';

const props = defineProps({
  ...getRadioGroupProps<T>(),
  label: String,
  innerClass: classProp,
});

defineEmits(getRadioGroupEmits<T>());

const id = useId();
const css = useCssModule('css');
const groupId = computed(() => `${id}-group`);

useRadioGroup<T>(props);

defineSlots<
  Slots<{
    default: { groupId: string };
    header: { groupId: string };
    info: { groupId: string };
  }>
>();

const wrapperClass = computed(() => {
  const isError = !!props.field?.errors?.length;

  return [
    css.radioGroup,
    isError && !props.disabled && !props.readonly && css.isError,
    props.readonly && css.isReadonly,
    props.disabled && css.isDisabled,
  ];
});

const label = computed(() => {
  let labelText = props.label;
  if (!labelText) {
    return null;
  }

  if (props.required) {
    labelText = labelText + ' *';
  }

  return labelText;
});
</script>

<template>
  <div
    :id="id"
    :class="wrapperClass"
    role="radiogroup"
    :aria-labelledby="label ? groupId : undefined"
    :aria-required="required"
  >
    <div
      v-if="label || $slots.header"
      :class="css.header"
    >
      <label
        v-if="label"
        :id="groupId"
        :class="css.label"
      >
        {{ label }}
      </label>

      <slot
        name="header"
        :group-id="groupId"
      />
    </div>

    <div :class="innerClass">
      <slot :group-id="groupId" />
    </div>

    <FieldInfo
      v-for="(error, index) in field?.errors"
      :key="index"
      type="error"
    >
      {{ error }}
    </FieldInfo>

    <slot
      name="info"
      :group-id="groupId"
    />
  </div>
</template>

<style lang="scss" module="css">
.radioGroup {
  display: flex;
  flex-direction: column;

  --label-color: #{$color-text};

  &.isError {
    --label-color: #{$color-patient-600};
  }

  &.isReadonly {
    --label-color: #{$color-text};
  }

  &.isDisabled {
    --label-color: #{$color-gray-600};
  }
}

.header {
  display: flex;
  align-items: center;
  gap: $spacing-04;
  font-size: $font-size-16;
  transition: color 0.15s ease-out;
  font-weight: $font-weight-500;
  color: var(--label-color);
}

.label {
  margin-right: auto;
  line-height: 2;
}
</style>
